import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { useCanManageConnections, useIsViewer } from "../lib/use-role.js";
import { useIntegrationCatalog, CATEGORY_META, groupProviders, type CatalogProvider, type CatalogSyncAction } from "../lib/integration-catalog.js";
import ConnectModal from "../components/ConnectModal.js";
import AdAccountPicker from "../components/AdAccountPicker.js";
import QuickBooksCompanyConfirm, { quickBooksConnectionNotice } from "../components/QuickBooksCompanyConfirm.js";
import {
  BUYGOODS_HISTORY_IMPORTED_LABEL,
  BUYGOODS_PENDING_LABEL,
  isImportedBuyGoodsHistory,
  isPendingBuyGoodsConnection,
} from "../lib/integration-readiness.js";
import { microsoftAdsOAuthErrorMessage } from "../lib/microsoft-ads-oauth-errors.js";
import { readOAuthClaimFragment } from "../lib/oauth-claim-fragment.js";
import { OAUTH_CLAIM_PROVIDERS, oauthClaimNext } from "../lib/oauth-claim-providers.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

export interface Integration {
  id: string;
  platform: string;
  status: "active" | "auth_expired" | "disabled" | "error";
  liveFinancialFeedProven: boolean | null;
  historyImported: boolean | null;
  lastSuccessfulSyncAt: string | null;
}

export type ConnectionModalRequest =
  | { platform: string; mode: "connect" }
  | { platform: "konnektive"; mode: "saved_reconnect"; integrationId: string };

/** Konnektive can retry its encrypted server-side login. Every other manual
 * provider keeps the existing blank connect form until it has the same safe
 * backend contract. */
export function connectionModalRequest(
  platform: string,
  integrationId?: string,
): ConnectionModalRequest {
  if (platform === "konnektive" && integrationId) {
    return { platform, mode: "saved_reconnect", integrationId };
  }
  return { platform, mode: "connect" };
}

/** Every OAuth callback's return param → display label. The param names are
 *  historical (meta_oauth predates the rest) — keep in sync with the
 *  integrations-*-oauth.ts callback routes in apps/api. */
const OAUTH_RETURN_LABELS: Record<string, string> = {
  meta_oauth: "Meta Ads",
  shopify_oauth: "Shopify",
  tiktok_oauth: "TikTok Ads",
  google_ads_oauth: "Google Ads",
  google_analytics_oauth: "Google Analytics",
  microsoft_ads_oauth: "Microsoft Ads",
  linkedin_ads_oauth: "LinkedIn Ads",
  pinterest_oauth: "Pinterest Ads",
  snapchat_oauth: "Snapchat Ads",
  quickbooks_oauth: "QuickBooks",
  xero_oauth: "Xero",
  freshbooks_oauth: "FreshBooks",
};

interface OAuthClaimResponse {
  ok: true;
  next?: "connected" | "choose";
}

function apiErrorText(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("detail" in error)) return null;
  const detail = (error as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object" || !("error" in detail)) return null;
  const message = (detail as { error?: unknown }).error;
  return typeof message === "string" ? message : null;
}

export default function ConnectionsPage(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const returnedFromQuickBooksDisconnect = typeof window !== "undefined"
    && window.location.pathname === "/connections/quickbooks-disconnected";

  const [openModal, setOpenModal] = useState<ConnectionModalRequest | null>(null);
  const [confirmQuickBooks, setConfirmQuickBooks] = useState(false);
  const [accountPicker, setAccountPicker] = useState<{
    platform: "meta_ads" | "google_ads" | "microsoft_ads";
    label: string;
  } | null>(null);

  const integrationsQ = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get<{ integrations: Integration[] }>("/integrations"),
  });
  // The full provider list — store, attribution, ESP, accounting, … — comes
  // from the backend registry, so this page never hardcodes platforms.
  const catalogQ = useIntegrationCatalog();
  const data = integrationsQ.data;
  const catalog = catalogQ.data;

  const toast = useToast();
  const canManageConnections = useCanManageConnections();

  useEffect(() => {
    if (!returnedFromQuickBooksDisconnect) return;
    window.history.replaceState({}, "", "/connections");
    void queryClient.invalidateQueries({ queryKey: ["integrations"] });
  }, [queryClient, returnedFromQuickBooksDisconnect]);

  // Returning from ANY platform's OAuth redirect (?…_oauth=…): finish the
  // flow — celebrate, open the ad-account picker, or explain what went wrong —
  // then scrub the params so a refresh doesn't replay it. Every callback
  // route gets a handler: a login that returns to a silent page reads as
  // "it broke", even when it worked.
  useEffect(() => {
    const claimReturn = OAUTH_CLAIM_PROVIDERS.map((provider) => ({
      provider,
      claim: readOAuthClaimFragment({
        hash: window.location.hash,
        statusKey: provider.statusKey,
        tokenKey: provider.tokenKey,
      }),
    })).find((entry) => entry.claim !== null);
    if (claimReturn?.claim) {
      const { provider, claim } = claimReturn;
      // Scrub the secret before the first network call. A URL fragment is not
      // sent to our web server or in referrers; removing it here also keeps it
      // out of copied URLs and browser history after this single use.
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${window.location.search}${claim.cleanHash}`,
      );
      if (!claim.claimToken) {
        toast.error(
          `We couldn't verify this ${provider.label} sign-in. Nothing was connected. Click Connect and sign in again.`,
        );
      } else {
        void api
          .post<OAuthClaimResponse>(`/integrations/${provider.platform}/oauth/claim`, {
            claimToken: claim.claimToken,
          })
          .then((result) => {
            queryClient.removeQueries({
              queryKey: [provider.platform, "oauth-ad-accounts"],
            });
            const next = oauthClaimNext(provider, result.next);
            if (next === "choose") {
              if (provider.platform === "quickbooks") {
                queryClient.removeQueries({ queryKey: ["quickbooks", "oauth-company"] });
                setConfirmQuickBooks(true);
                return;
              }
              if (!provider.pickerPlatform) {
                toast.error(
                  `We couldn't finish this ${provider.label} sign-in. Nothing was connected. Click Connect and sign in again.`,
                );
                return;
              }
              setAccountPicker({
                platform: provider.pickerPlatform,
                label: provider.label,
              });
              return;
            }
            relockFinancialQueries(queryClient);
            void queryClient.invalidateQueries({ queryKey: ["integrations"] });
            toast.success(`${provider.label} connected — pulling your data now.`);
          })
          .catch((error: unknown) => {
            toast.error(
              apiErrorText(error) ??
                `We couldn't verify this ${provider.label} sign-in. Nothing was connected. Click Connect and sign in again.`,
            );
          });
      }
    }

    const params = new URLSearchParams(window.location.search);
    const hits = Object.keys(OAUTH_RETURN_LABELS).filter((k) => params.get(k));
    if (hits.length === 0) return;
    const statuses = new Map(hits.map((k) => [k, params.get(k)!] as const));
    const reason = params.get("reason");
    for (const k of hits) params.delete(k);
    params.delete("reason");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);

    for (const [key, status] of statuses) {
      const label = OAUTH_RETURN_LABELS[key]!;
      // A legacy return URL is not evidence that the company was confirmed.
      // QuickBooks must always show the authenticated pending-company choice.
      if (key === "quickbooks_oauth" && (status === "choose" || status === "connected")) {
        queryClient.removeQueries({ queryKey: ["quickbooks", "oauth-company"] });
        setConfirmQuickBooks(true);
        continue;
      }
      if (status === "connected") {
        relockFinancialQueries(queryClient);
        void queryClient.invalidateQueries({ queryKey: ["integrations"] });
        toast.success(`${label} connected — pulling your data now.`);
      } else if (status === "choose") {
        if (key === "meta_oauth") {
          setAccountPicker({ platform: "meta_ads", label: "Meta Ads" });
        } else if (key === "google_ads_oauth") {
          setAccountPicker({ platform: "google_ads", label: "Google Ads" });
        } else if (key === "microsoft_ads_oauth") {
          setAccountPicker({ platform: "microsoft_ads", label: "Microsoft Ads" });
        }
      } else if (status === "cancelled") {
        toast.error(`${label} login was cancelled — nothing was connected.`);
      } else if (status === "error") {
        const microsoftAdsError =
          key === "microsoft_ads_oauth" ? microsoftAdsOAuthErrorMessage(reason) : null;
        if (microsoftAdsError) {
          toast.error(microsoftAdsError);
        } else if (key === "meta_oauth" && reason === "no_ad_accounts") {
          toast.error("That Facebook login can't see any ad accounts. Log in with a user who has access to the ad account.");
        } else if (key === "linkedin_ads_oauth" && reason === "needs_allowlist") {
          toast.error(
            "Your LinkedIn login worked — LinkedIn just makes us approve your ad account on our end first. Use \"Submit a bug\" in the left menu with your ad account name; we'll approve it (usually same day), then log in once more.",
          );
        } else {
          toast.error(`Couldn't finish the ${label} login. Try again. If it keeps failing, send it to us through Submit a bug.`);
        }
      }
    }
  }, []);

  const disconnect = useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/${id}`),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: () => toast.error("Couldn't disconnect. Try again."),
  });
  const requiredLoad = pageLoadState(integrationsQ, catalogQ);

  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your connections"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }

  const connected = new Map((data?.integrations ?? []).map((i) => [i.platform, i] as const));

  // Group by the registry's category, in a founder-friendly order (shared with
  // the onboarding connect drawer). Within a group: connected first, then
  // connectable, "coming soon" last.
  const orderedGroups = dedupeConnectionGroups(
    groupProviders(
      catalog?.providers ?? [],
      (p) => (connected.has(p.platform) ? 0 : p.comingSoon ? 2 : 1),
    ),
  );

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24 }}>Connections</h1>
      <p style={{ color: "var(--body)", marginTop: 6 }}>
        Connect the platforms that power your numbers.
      </p>

      {returnedFromQuickBooksDisconnect && (
        <div role="status" style={{ marginTop: 18, padding: 14, border: "1px solid var(--warn, #b5820a)", borderRadius: 8, background: "var(--surface-2)", color: "var(--body)", lineHeight: 1.5 }}>
          QuickBooks says this app was disconnected. New QuickBooks imports cannot continue. If the card below still says Connected, click Disconnect once to finish closing the connection here. Saved QuickBooks reports will not be used after the connection is inactive.
        </div>
      )}

      {(integrationsQ.isLoading || catalogQ.isLoading) && <p style={{ marginTop: 24 }}>Loading…</p>}

      {orderedGroups.map(([category, list]) => (
        <section key={category} style={{ marginTop: 28 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>{CATEGORY_META[category]?.label ?? "More"}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, marginTop: 14 }}>
            {list.map((provider) => (
              <IntegrationCard
                key={provider.platform}
                provider={provider}
                integration={connected.get(provider.platform)}
                canManage={canManageConnections}
                onConnect={(request) => setOpenModal(request)}
                onDisconnect={(id) => disconnect.mutate(id)}
                disconnecting={disconnect.isPending}
              />
            ))}
          </div>
        </section>
      ))}

      {catalog && <RequestConnectionCard />}

      {openModal && (
        <ConnectModal
          platform={openModal.platform}
          provider={catalog?.providers.find((p) => p.platform === openModal.platform)}
          reconnect={openModal.mode === "saved_reconnect"
            ? { integrationId: openModal.integrationId }
            : undefined}
          onClose={() => setOpenModal(null)}
          onSuccess={() => {
            /* invalidation handled in modal */
          }}
        />
      )}

      {accountPicker && (
        <AdAccountPicker
          {...accountPicker}
          onClose={() => setAccountPicker(null)}
          onConnected={() => toast.success(`${accountPicker.label} connected. We're pulling your ad spend history now.`)}
        />
      )}
      {confirmQuickBooks && canManageConnections && <QuickBooksCompanyConfirm
        onClose={() => setConfirmQuickBooks(false)}
        onConnected={(result) => {
          const notice = quickBooksConnectionNotice(result.importStarted);
          toast[notice.kind](notice.message);
        }}
      />}
    </div>
  );
}

/** The onboarding catalog can intentionally list a dual-role provider in two
 * sections. On the client Connections page, one card is enough. */
export function dedupeConnectionGroups(
  groups: Array<[string, CatalogProvider[]]>,
): Array<[string, CatalogProvider[]]> {
  const seen = new Set<string>();
  return groups.flatMap(([category, providers]) => {
    const unique = providers.filter((provider) => {
      if (seen.has(provider.platform)) return false;
      seen.add(provider.platform);
      return true;
    });
    return unique.length > 0 ? [[category, unique] as [string, CatalogProvider[]]] : [];
  });
}

/** Category display order + founder-friendly labels. Keys match the backend
 *  registry's IntegrationCategory values; anything unrecognized lands in the
 *  "_other" bucket so a future category can never disappear from this page. */
/** "Don't see one of your connections here?" — stores the request durably
 *  server-side (and emails support when configured). */
function RequestConnectionCard(): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const isViewer = useIsViewer();
  const [open, setOpen] = useState(false);
  const [platformName, setPlatformName] = useState("");
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");

  const submit = useMutation({
    mutationFn: () => api.post("/integrations/connection-request", { platformName, note, contactEmail: email }),
    onSuccess: () => {
      setOpen(false);
      setPlatformName("");
      setNote("");
      setEmail("");
      toast.success("Request received — we'll let you know when it's available.");
    },
    onError: () => toast.error("Couldn't send the request. Try again."),
  });

  if (isViewer) return <></>;

  return (
    <section style={{ marginTop: 32, marginBottom: 8 }}>
      <div style={{ ...cardStyles.card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <span style={cardStyles.title}>Need another platform?</span>
        <button style={cardStyles.connectButton} onClick={() => setOpen(true)}>
          Request it
        </button>
      </div>

      {open && (
        <div style={requestStyles.backdrop} onClick={() => setOpen(false)}>
          <div style={requestStyles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Request a connection</h2>
            <form
              style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}
              onSubmit={(e) => {
                e.preventDefault();
                submit.mutate();
              }}
            >
              <label style={requestStyles.label}>
                What platform do you use?
                <input
                  style={requestStyles.input}
                  value={platformName}
                  onChange={(e) => setPlatformName(e.target.value)}
                  placeholder="e.g. Squarespace, Braintree, Attribuly…"
                  autoFocus
                />
              </label>
              <label style={requestStyles.label}>
                What do you use it for? <span style={{ opacity: 0.6 }}>(optional)</span>
                <input
                  style={requestStyles.input}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. it's our store / our SMS tool / our payment processor"
                />
              </label>
              <label style={requestStyles.label}>
                Your email <span style={{ opacity: 0.6 }}>(optional — so we can tell you when it's ready)</span>
                <input
                  style={requestStyles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                <button type="button" style={cardStyles.secondaryButton} onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <button type="submit" style={cardStyles.connectButton} disabled={!platformName.trim() || submit.isPending}>
                  {submit.isPending ? "Sending…" : "Send request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

const requestStyles: Record<string, React.CSSProperties> = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 24, width: 440, maxWidth: "95vw" },
  label: { display: "flex", flexDirection: "column", fontSize: 13, color: "var(--body)", gap: 6 },
  input: { background: "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 10px", borderRadius: 6, fontSize: 14, fontFamily: "inherit" },
};

/**
 * A few shared catalog descriptions need setup detail during onboarding, but
 * that same detail makes the Connections page noisy. Keep the shorter client
 * summary here so the shared onboarding catalog stays unchanged.
 */
const CONNECTION_CARD_DESCRIPTIONS: Partial<Record<string, string>> = {
  ontraport: "Connects existing Ontraport contacts for customer updates.",
  hubspot: "Pulls HubSpot orders and customers, then matches them to existing contacts.",
  activecampaign: "Connects existing ActiveCampaign contacts for customer updates.",
  keap: "Connects existing Keap contacts for customer updates.",
  gohighlevel: "Connects existing GoHighLevel contacts for customer updates.",
  attentive: "Sends customer segments to Attentive for SMS.",
  quickbooks: "Pulls monthly expenses from your QuickBooks P&L for review.",
  xero: "Pulls monthly expenses from your Xero P&L for review.",
  freshbooks: "Pulls monthly expenses from your FreshBooks P&L for review.",
  stripe_fees: "Replaces estimated processing fees with Stripe's settled fees on existing orders.",
  paypal_fees: "Replaces estimated processing fees with PayPal's settled fees on existing orders.",
  shipstation: "Replaces estimated shipping costs with ShipStation postage on existing orders.",
  recharge: "Tracks MRR, churn, upcoming bills, and failed payments from Recharge.",
  skio: "Tracks MRR, churn, upcoming bills, and failed payments from Skio.",
  bold_subscriptions: "Tracks MRR, churn, and upcoming bills from Bold Subscriptions.",
  mixpanel: "Pulls daily event counts from Mixpanel for funnel analysis.",
  meta_ads: "Pulls Facebook and Instagram ad spend by campaign.",
  microsoft_ads: "Pulls Microsoft Ads spend by campaign.",
  google_ads: "Pulls Google Ads spend by campaign.",
  linkedin_ads: "Pulls LinkedIn Ads spend by campaign.",
  taboola: "Pulls Taboola ad spend by campaign.",
  tiktok_ads: "Pulls TikTok ad spend by campaign.",
  amazon_ads: "Pulls Amazon ad spend by campaign.",
  pinterest_ads: "Pulls Pinterest ad spend by campaign.",
  snapchat_ads: "Pulls Snapchat ad spend by campaign.",
  hasoffers: "Pulls affiliate sales and commissions from HasOffers.",
  clickbank: "Pulls affiliate sales and commissions from ClickBank.",
  everflow: "Pulls affiliate sales and commissions from Everflow.",
  refersion: "Pulls affiliate sales and commissions from Refersion.",
  impact: "Pulls affiliate sales and commissions from Impact.com.",
  awin: "Pulls affiliate sales and commissions from Awin.",
  digistore24: "Pulls sales and affiliate commissions from Digistore24.",
  amazon: "Pulls orders from Amazon Seller Central.",
  google_analytics: "Pulls sessions, key events, and traffic by landing page and channel from Google Analytics.",
  konnektive: "Pulls orders, rebills, refunds, and customers from Konnektive or Checkout Champ.",
  buygoods: "Receives new BuyGoods orders, rebills, refunds, and chargebacks. Past orders require an export.",
};

export function connectionCardDescription(provider: CatalogProvider): string {
  return CONNECTION_CARD_DESCRIPTIONS[provider.platform] ?? provider.description;
}

export function IntegrationCard({
  provider,
  integration,
  onConnect,
  onDisconnect,
  disconnecting,
  canManage = true,
}: {
  provider: CatalogProvider;
  integration: Integration | undefined;
  onConnect: (request: ConnectionModalRequest) => void;
  onDisconnect: (id: string) => void;
  disconnecting: boolean;
  canManage?: boolean;
}): React.ReactElement {
  const status = integration?.status;
  const supported = !provider.comingSoon;
  const waitingForFirstBuyGoodsSale = integration
    ? isPendingBuyGoodsConnection(integration)
    : false;
  const importedBuyGoodsHistory = integration
    ? isImportedBuyGoodsHistory(integration)
    : false;
  return (
    <div style={cardStyles.card}>
      <div style={cardStyles.titleRow}>
        <span style={cardStyles.title}>{provider.label}</span>
        {integration && status === "active" && (
          <StatusPill
            text={waitingForFirstBuyGoodsSale
              ? BUYGOODS_PENDING_LABEL
              : importedBuyGoodsHistory
                ? BUYGOODS_HISTORY_IMPORTED_LABEL
                : "Connected"}
            color={waitingForFirstBuyGoodsSale ? "var(--c2)" : "var(--good)"}
          />
        )}
        {integration && status === "auth_expired" && (
          <StatusPill text="Reconnect needed" color="var(--c2)" />
        )}
        {integration && status === "error" && (
          <StatusPill text="Update paused" color="var(--bad)" />
        )}
        {integration && status === "disabled" && (
          <StatusPill text="Disconnected" color="var(--body)" />
        )}
        {!integration && !supported && (
          <StatusPill text="Coming soon" color="var(--body)" />
        )}
        {!integration && supported && (
          <StatusPill text="Not connected" color="var(--body)" />
        )}
      </div>
      <p style={cardStyles.description}>{connectionCardDescription(provider)}</p>

      {importedBuyGoodsHistory && (
        <p style={cardStyles.meta}>Live updates will verify automatically with the next sale.</p>
      )}

      {integration && !waitingForFirstBuyGoodsSale && integration.lastSuccessfulSyncAt && (
        <p style={cardStyles.meta}>
          Data updated {formatRelative(integration.lastSuccessfulSyncAt)}
        </p>
      )}

      <div style={{ marginTop: 14 }}>
        {!integration && supported && !provider.oauthPending && canManage && (
          <button
            style={cardStyles.connectButton}
            onClick={() => onConnect(connectionModalRequest(provider.platform))}
          >
            Connect
          </button>
        )}
        {!integration && supported && provider.oauthPending && canManage && (
          <button
            style={cardStyles.connectButton}
            onClick={() => onConnect(connectionModalRequest(provider.platform))}
          >
            Request connection
          </button>
        )}
        {integration && status === "active" && (
          <>
            {canManage && (
              <SyncActions
                provider={provider}
                disconnecting={disconnecting}
                onDisconnect={() => {
                  // A mis-click silently stops a live connection and numbers go
                  // stale. Confirm first (backend soft-disables, so the copy is
                  // accurate: data is kept and reconnecting is one click).
                  if (window.confirm(`Disconnect ${provider.label}? Syncing will pause and these numbers stop updating. Your already-imported data stays, and you can reconnect anytime.`)) {
                    onDisconnect(integration.id);
                  }
                }}
              />
            )}
          </>
        )}
        {integration && status === "auth_expired" && canManage && (
          <button
            style={cardStyles.connectButton}
            onClick={() => onConnect(connectionModalRequest(provider.platform, integration.id))}
          >
            Reconnect
          </button>
        )}
        {integration && (status === "disabled" || status === "error") && canManage && (
          /* Konnektive first retries the encrypted saved login. Other
             providers keep their existing blank reconnect form. */
          <button
            style={cardStyles.connectButton}
            onClick={() => onConnect(connectionModalRequest(provider.platform, integration.id))}
          >
            Reconnect
          </button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ text, color }: { text: string; color: string }): React.ReactElement {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 12,
        // color is a CSS var now, so alpha via color-mix (a "22" hex suffix only works on raw hex)
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        color,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {text}
    </span>
  );
}

/**
 * Sync + disconnect controls for a connected provider — driven entirely by
 * the provider's registry sync actions. ONE primary button shows inline (the
 * registry's `primary` flag decides which — for affiliate networks that IS
 * the history pull); every other action plus Disconnect lives behind a
 * compact "Manage" disclosure. Each sync posts to the generic
 * POST /sync-jobs/:platform/:action route. Only owners and admins see it.
 */
function SyncActions({
  provider,
  onDisconnect,
  disconnecting,
}: {
  provider: CatalogProvider;
  onDisconnect: () => void;
  disconnecting: boolean;
}): React.ReactElement | null {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isViewer = useIsViewer();
  const [busy, setBusy] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  if (isViewer) return null;

  const run = async (action: CatalogSyncAction): Promise<void> => {
    // Heavy = a full-history re-pull. Same window.confirm pattern as
    // Disconnect, with duration-honest copy, so nobody kicks off an
    // hour-long job thinking it's a quick refresh.
    if (
      action.heavy &&
      !window.confirm(
        `Re-import everything from ${provider.label}? This re-downloads your full history and can take a while — big accounts can run an hour or more. You can keep using the dashboard while it works; numbers fill in as it goes.`,
      )
    ) {
      return;
    }
    setBusy(action.id);
    try {
      await api.post(`/sync-jobs/${provider.platform}/${action.id}`);
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast.success("Sync started — your numbers update over the next few minutes.");
    } catch {
      toast.error("Couldn't start the sync. Try again in a minute.");
    } finally {
      setBusy(null);
    }
  };

  const primaries = provider.syncActions.filter((a) => a.primary);
  const rest = provider.syncActions.filter((a) => !a.primary);

  return (
    <div style={{ marginTop: 10 }}>
      {primaries.map((action) => (
        <div key={action.id}>
          <button style={cardStyles.actionButton} onClick={() => void run(action)} disabled={busy !== null}>
            {busy === action.id ? action.pendingLabel : action.label}
          </button>
        </div>
      ))}
      <button
        type="button"
        style={cardStyles.moreToggle}
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((o) => !o)}
      >
        {moreOpen ? "Close" : "Manage"}
      </button>
      {moreOpen && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {rest.map((action) => (
            <button key={action.id} style={cardStyles.moreActionButton} onClick={() => void run(action)} disabled={busy !== null}>
              {busy === action.id ? action.pendingLabel : action.label}
            </button>
          ))}
          {/* Disconnect lives last, past a divider and in the danger color —
              it must never read as a fifth sync option. */}
          <div style={cardStyles.dangerZone}>
            <button style={cardStyles.dangerButton} onClick={onDisconnect} disabled={disconnecting}>
              Disconnect
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const diffMs = Date.now() - ts;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

const cardStyles: Record<string, React.CSSProperties> = {
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 18,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
  },
  description: {
    color: "var(--body)",
    fontSize: 13,
    lineHeight: 1.45,
    margin: "8px 0 0",
  },
  connectButton: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    marginTop: 0,
  },
  secondaryButton: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    marginTop: 12,
    marginLeft: 8,
  },
  actionButton: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
  },
  /* Rows inside the "Manage" disclosure — same quiet look as
     secondaryButton but without its inline-row margins. */
  moreActionButton: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  },
  moreToggle: {
    background: "transparent",
    border: "none",
    padding: 0,
    marginTop: 8,
    fontSize: 12,
    color: "var(--body)",
    cursor: "pointer",
  },
  dangerZone: {
    borderTop: "1px solid var(--border)",
    marginTop: 8,
    paddingTop: 8,
  },
  dangerButton: {
    background: "transparent",
    border: "none",
    padding: 0,
    fontSize: 13,
    color: "var(--bad)",
    cursor: "pointer",
  },
  meta: {
    fontSize: 12,
    color: "var(--body)",
    marginTop: 8,
    marginBottom: 4,
  },
};
